import { EventPageType, PageKind } from '@graphql/generated-base';
import type { PageContentType } from '@graphql/generated-base';
import type { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';

const MIGRATED_CUSTOM_PAGE_SLUGS_LIST: ReadonlyArray<MigratedCustomPageSlug> = ['story', 'faq', 'registrylinks', 'travel', 'vip', 'welcome', 'schedule'];
export const MIGRATED_CUSTOM_PAGE_SLUGS: ReadonlySet<MigratedCustomPageSlug> = new Set(MIGRATED_CUSTOM_PAGE_SLUGS_LIST);

// Mapping from migrated page slugs to their corresponding legacy EventPageType.
// Note: 'registrylinks' slug maps to EventPageType.registry — legacy name mismatch.
const SLUG_TO_LEGACY_PAGE_TYPE: Readonly<Partial<Record<MigratedCustomPageSlug, EventPageType>>> = {
  story: EventPageType.story,
  faq: EventPageType.faq,
  registrylinks: EventPageType.registry,
  travel: EventPageType.travel,
  vip: EventPageType.vip,
  welcome: EventPageType.welcome,
  schedule: EventPageType.schedule,
};

export const getLegacyPageType = (slug: MigratedCustomPageSlug): EventPageType | undefined => SLUG_TO_LEGACY_PAGE_TYPE[slug];

// Mapping from EventPageType to canonical slug for migrated pages.
// e.g., EventPageType.vip → 'vip' (canonical even if the pageSlug differs).
const PAGE_TYPE_TO_SLUG: Readonly<Partial<Record<EventPageType, MigratedCustomPageSlug>>> = {
  [EventPageType.story]: 'story',
  [EventPageType.faq]: 'faq',
  [EventPageType.registry]: 'registrylinks',
  [EventPageType.travel]: 'travel',
  [EventPageType.vip]: 'vip',
  [EventPageType.welcome]: 'welcome',
  [EventPageType.schedule]: 'schedule',
};

export const getMigratedPageSlug = (pageType: EventPageType): MigratedCustomPageSlug | undefined => PAGE_TYPE_TO_SLUG[pageType];

// True for the legacy registry page (`EventPageType.registry`) and its migrated form
// (`EventPageType.custom` with a slug that canonicalizes to 'registrylinks' AND the
// `useRegistryLinksAsCustomPage` event feature flag enabled). Without the flag check
// a user-created custom page that happens to be slugged `registrylinks` would be
// mis-identified as the migrated registry. Pass `isMigratedCustomPageEnabled` from
// `useMigratedCustomPageFeatureFlags()`.
export const isRegistryPage = (
  pageType: EventPageType | null | undefined,
  pageSlug: string | null | undefined,
  isMigratedCustomPageEnabled: (slug: string) => boolean,
): boolean => {
  if (pageType === EventPageType.registry) return true;
  if (pageType === EventPageType.custom && toCanonicalMigratedSlug(pageSlug) === 'registrylinks') {
    return isMigratedCustomPageEnabled('registrylinks');
  }
  return false;
};

// Canonicalizes a data-slug or EventPageType value to its MigratedCustomPageSlug.
// Needed because migrated registrylinks carries pageSlug='registry' in data while
// the canonical (flag-keyed / URL) slug is 'registrylinks'.
export const toCanonicalMigratedSlug = (pageSlug: string | undefined | null): MigratedCustomPageSlug | undefined => {
  if (!pageSlug) return undefined;
  const lower = pageSlug.toLowerCase();
  if (MIGRATED_CUSTOM_PAGE_SLUGS.has(lower as MigratedCustomPageSlug)) {
    return lower as MigratedCustomPageSlug;
  }
  return PAGE_TYPE_TO_SLUG[lower as EventPageType];
};

type PageWithTypeAndSlug = Readonly<{
  pageSlug: string;
  type?: EventPageType | null;
  // CORE-6470: page-kind decisions now branch on `pageKind` (systemLegacy | systemCustom |
  // userCustom) rather than `type` + `contentType`. `type`/`contentType` remain on the type
  // transitionally for non-kind callers, but are no longer read for kind classification.
  pageKind?: PageKind | null;
  contentType?: PageContentType | null;
}>;

// CORE-6456: a user-created custom page squatting a built-in/migrated slug
// (e.g. a page the user named "Schedule"). It must route/render as a plain
// custom page, not as the migrated/native page.
// CORE-6470: identified by pageKind === userCustom (was type === custom + contentType === userGenerated).
export const isUserGeneratedMigratedSquatter = (page: PageWithTypeAndSlug): boolean => page.pageKind === PageKind.userCustom && !!toCanonicalMigratedSlug(page.pageSlug);

// These custom pages always use native routing/entry even though they're EventPageType.custom.
const ALWAYS_NATIVE_PAGE_SLUGS: ReadonlySet<string> = new Set(['accommodations']);

/**
 * Determines if a page should use the native/dictionary entry (with correct icon/path)
 * instead of the generic custom page handler.
 */
type PageContainerInfo =
  | Readonly<{
      pageKind?: PageKind | null;
      contentType?: PageContentType | null;
    }>
  | null
  | undefined;

/**
 * Checks whether an event has a system-created (migrated/internal) custom page for the given slug.
 * CORE-6470: expressed via `pageKind` instead of the raw `type` + `contentType` matrix, but
 * behavior-preserving — true iff BOTH:
 *   1. a custom page record exists for the slug (`pageKind !== systemLegacy`, i.e. the old
 *      `type === custom` check — note the original did NOT constrain the record's own contentType), and
 *   2. the corresponding PageContainer is system-created (`pageKind === systemCustom`, i.e. the
 *      old `contentType === default` check).
 * Kept as an AND (not "either side is systemCustom"): a `default` container with no matching page
 * record — or a custom page record whose container is not `default` — must NOT count as migrated,
 * otherwise the admin would flip from the legacy editor to the custom editor.
 *
 * This check is data-driven (not flag-driven) so the custom page editor stays active
 * even if the feature flag is later toggled off after migration.
 *
 * The slug is canonicalized so callers can pass either the URL/canonical form
 * (e.g., 'registrylinks') or the underlying container/data form (e.g., 'registry').
 */
export const hasMigratedCustomPage = (pages: ReadonlyArray<PageWithTypeAndSlug> | undefined, slug: string, pageContainer: PageContainerInfo): boolean => {
  const targetCanonical = toCanonicalMigratedSlug(slug);
  if (!targetCanonical) return false;
  const hasCustomPageRecord = pages?.some(p => p.pageKind !== PageKind.systemLegacy && toCanonicalMigratedSlug(p.pageSlug) === targetCanonical) ?? false;
  const containerIsSystemCustom = pageContainer?.pageKind === PageKind.systemCustom;
  return hasCustomPageRecord && containerIsSystemCustom;
};

// Returns the PageContainer `slug` used by the backend for a given URL/canonical slug.
// Most migrations store the container under the same slug as the URL segment
// (e.g. 'travel' → 'travel'); registrylinks stores it under 'registry'
// (EventPageType.registry) for backend replacement matching.
// Safe to call with any slug — returns the input unchanged when no translation applies.
export const toPageContainerSlug = (pageSlug: string | undefined | null): string => {
  if (!pageSlug) return '';
  const canonical = toCanonicalMigratedSlug(pageSlug);
  const legacyType = canonical ? SLUG_TO_LEGACY_PAGE_TYPE[canonical] : undefined;
  return (legacyType as string | undefined) ?? pageSlug;
};

// Legacy editor lives in joy-web with no SSR proxy, so flag-off rollback has to invert the
// filter on the client.
const ROLLBACK_AWARE_MIGRATED_SLUGS: ReadonlySet<MigratedCustomPageSlug> = new Set<MigratedCustomPageSlug>(['registrylinks', 'travel']);

// True for migrated slugs whose flag-off rollback keeps the legacy native page active;
// false for slugs that fall back to ordinary custom-page rendering on flag-off.
export const isRollbackAwareMigratedSlug = (slug: string | undefined | null): boolean => {
  const canonical = toCanonicalMigratedSlug(slug);
  return !!canonical && ROLLBACK_AWARE_MIGRATED_SLUGS.has(canonical);
};

type FilterMigratedLegacyPagesOptions = Readonly<{
  isMigratedCustomPageEnabled: (slug: MigratedCustomPageSlug) => boolean;
  // True while feature flags are still loading. During load the callback returns false for
  // every slug — indistinguishable from "flag off" — so treat as flag-on (drop legacy) to
  // avoid a momentary duplicate-row flicker before the real flag state arrives.
  isFeatureFlagsLoading?: boolean;
}>;
// Behavior:
// - flag on (or loading) → drop the legacy row, keep the migrated custom row.
// - flag off + rollback-aware slug (`registrylinks`, `travel`) → drop the migrated custom
//   row, keep the legacy native page.
// - flag off + non-rollback-aware slug → keep both. The legacy row routes through the
//   native dictionary entry; the migrated custom row falls through to the generic
//   custom-page handler (`/edit/website/page/{pageSlug}`) — see `shouldUseNativePageEntry`.
export const filterMigratedLegacyPages = <T extends PageWithTypeAndSlug>(pages: ReadonlyArray<T>, options: FilterMigratedLegacyPagesOptions): ReadonlyArray<T> => {
  const legacyTypesToFilter = new Set<EventPageType>();
  const customSlugsToFilter = new Set<MigratedCustomPageSlug>();

  pages.forEach(page => {
    // CORE-6470: only custom pages (systemCustom | userCustom) are migration candidates;
    // systemLegacy rows are the legacy native pages we may drop, never the trigger.
    if (page.pageKind === PageKind.systemLegacy) return;
    // CORE-6456: a userGenerated squatter must never cause the native/legacy page to be
    // filtered out — it's a plain custom page that merely shares the slug.
    if (isUserGeneratedMigratedSquatter(page)) return;
    const canonical = toCanonicalMigratedSlug(page.pageSlug);
    if (!canonical) return;
    const legacyType = SLUG_TO_LEGACY_PAGE_TYPE[canonical];
    if (!legacyType) return;

    const isRollbackAware = ROLLBACK_AWARE_MIGRATED_SLUGS.has(canonical);
    const isFlagOff = !options.isFeatureFlagsLoading && options.isMigratedCustomPageEnabled(canonical) === false;

    if (isFlagOff) {
      if (isRollbackAware) customSlugsToFilter.add(canonical);
      // Non-rollback-aware + flag off: keep both rows.
      return;
    }
    legacyTypesToFilter.add(legacyType);
  });

  if (legacyTypesToFilter.size === 0 && customSlugsToFilter.size === 0) return pages;

  return pages.filter(page => {
    // CORE-6470: custom rows (systemCustom | userCustom) are filtered by their canonical slug;
    // systemLegacy rows are filtered by their EventPageType.
    if (page.pageKind !== PageKind.systemLegacy) {
      const canonical = toCanonicalMigratedSlug(page.pageSlug);
      return !canonical || !customSlugsToFilter.has(canonical);
    }
    return !legacyTypesToFilter.has(page.type as EventPageType);
  });
};

type ShouldUseNativePageEntryOptions = Readonly<{
  isMigratedCustomPageEnabled: (slug: MigratedCustomPageSlug) => boolean;
  isFeatureFlagsLoading?: boolean;
}>;

export const shouldUseNativePageEntry = (page: PageWithTypeAndSlug, options: ShouldUseNativePageEntryOptions): boolean => {
  // CORE-6456: a userGenerated page squatting a migrated slug is a plain custom page —
  // it must be id-keyed, never routed through the native dictionary entry.
  if (isUserGeneratedMigratedSquatter(page)) return false;

  // Always use native entry for specific slugs (e.g., accommodations)
  if (ALWAYS_NATIVE_PAGE_SLUGS.has(page.pageSlug)) return true;

  // CORE-6470: systemLegacy (built-in) pages always use native entry.
  if (page.pageKind === PageKind.systemLegacy) return true;

  const canonical = toCanonicalMigratedSlug(page.pageSlug);
  // Regular custom pages (no migrated canonical) are id-keyed, not native.
  if (!canonical) return false;

  // Rollback-aware slugs: `filterMigratedLegacyPages` drops the custom row on flag-off,
  // so reaching here means flag is on — use native entry.
  if (ROLLBACK_AWARE_MIGRATED_SLUGS.has(canonical)) return true;

  // While flags are loading, treat as flag-on (matches the helper's loading behavior)
  // so we don't briefly mis-route a migrated page through the generic custom path.
  if (options.isFeatureFlagsLoading) return true;

  // Non-rollback-aware migrated custom pages: native entry only when the flag is on.
  // Flag off → render as a generic custom page (`/edit/website/page/{pageSlug}`) so it
  // doesn't collide with the legacy native dictionary entry that `filterMigratedLegacyPages`
  // intentionally preserves alongside it.
  return options.isMigratedCustomPageEnabled(canonical);
};
